import { AuthProvider } from "@descope/react-sdk";
import { CssBaseline, GlobalStyles } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { LicenseInfo } from "@mui/x-license";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import RootErrorBoundary from "./components/errors/RootErrorBoundary";
import AlertSnackbar from "./components/global/components/AlertSnackbar";
import store from "./redux/store";
import appRoutes from "./routes";
import { lightModeTheme } from "./theme";

// Filter only source map warnings in console
const originalConsoleWarn = console.warn;
console.warn = function (msg: unknown, ...rest: unknown[]) {
	if (msg && typeof msg === "string" && msg.includes("Failed to parse source map")) {
		return;
	}
	(originalConsoleWarn as (message?: unknown, ...optionalParams: unknown[]) => void)(msg, ...rest);
};

const router = createBrowserRouter(appRoutes);

// One canonical scrollbar for the ENTIRE app: white trough, brand-teal thin
// pill thumb (the 3px white border + content-box clip makes the colored pill
// thin inside the white track). Applied globally so every scrollbar — default
// browser ones and styled containers alike — looks identical.
const APP_SCROLLBAR_GLOBAL = {
	"*::-webkit-scrollbar": { width: "12px", height: "12px", backgroundColor: "#FFFFFF" },
	"*::-webkit-scrollbar-track": { backgroundColor: "#FFFFFF" },
	"*::-webkit-scrollbar-thumb": {
		borderRadius: "999px",
		backgroundColor: "rgba(2, 213, 171, 0.55)",
		border: "3px solid #FFFFFF",
		backgroundClip: "content-box",
	},
	"*::-webkit-scrollbar-thumb:hover": { backgroundColor: "rgba(2, 213, 171, 0.85)" },
	"*::-webkit-scrollbar-corner": { backgroundColor: "#FFFFFF" },
	// The DataGrid filter panel renders in a portal at <body>, OUTSIDE the grid
	// root — so grid-scoped styles never reach it. Style its destructive
	// "Remove all" footer button (the last one) red globally so it reads as
	// destructive vs. the brand-teal "Add filter". Applies to every grid's panel.
	".MuiDataGrid-panelFooter button:last-of-type": {
		color: "#E03030",
		"& .MuiSvgIcon-root": { color: "#E03030" },
		"&:hover": { backgroundColor: "rgba(224, 48, 48, 0.08)" },
	},
} as const;

const rootEl = document.getElementById("root");
if (!rootEl) {
	throw new Error("Root element #root not found");
}
const root = ReactDOM.createRoot(rootEl);

// MUI X Pro license key. The license API expects this client-side (it
// verifies the key at runtime against the licensed domain). Read from an
// env var so the new team can rotate / swap their key without changing
// source. Falls back to the embedded key for local dev convenience.
LicenseInfo.setLicenseKey(
	import.meta.env.VITE_MUI_X_LICENSE_KEY
	?? "0ab7d3e0d20a678a275ae4a4f6a26c50Tz0xMjMwNTAsRT0xNzk3MTE5OTk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMS0yMDI2LFE9MixBVD1tdWx0aSxLVj0y"
);

root.render(
	<RootErrorBoundary>
		<AuthProvider projectId={import.meta.env.VITE_DESCOPE_PROJECT_ID}>
			<Provider store={store}>
				<ThemeProvider theme={lightModeTheme}>
					<CssBaseline />
					<GlobalStyles styles={APP_SCROLLBAR_GLOBAL} />
					<AlertSnackbar />
					<RouterProvider router={router} />
				</ThemeProvider>
			</Provider>
		</AuthProvider>
	</RootErrorBoundary>
);